// ROI Categories and Process Options
// Comprehensive list of 30+ common SMB processes for AI automation opportunities
// Each process includes a pre-assigned difficulty score (1=easy, 5=very difficult to automate)

export const ROI_CATEGORIES = {
  operations: {
    id: 'operations',
    label: 'Operations & Efficiency',
    subtitle: 'Finance / Admin / Scheduling',
    color: 'primary',
    processes: [
      { name: 'Invoice & payment processing', difficulty: 2 },
      { name: 'Payroll prep or reconciliation', difficulty: 3 },
      { name: 'Inventory tracking & restocking', difficulty: 2 },
      { name: 'Scheduling coordination', difficulty: 1 },
      { name: 'Time tracking or approvals', difficulty: 1 },
      { name: 'Project intake or assignment', difficulty: 2 },
      { name: 'Document filing or routing', difficulty: 1 },
      { name: 'Vendor onboarding & renewals', difficulty: 3 },
      'Custom...'
    ]
  },
  customer: {
    id: 'customer',
    label: 'Customer Experience',
    subtitle: 'Support & Communication',
    color: 'primary',
    processes: [
      { name: 'Responding to customer questions', difficulty: 2 },
      { name: 'Managing support tickets or chats', difficulty: 2 },
      { name: 'Gathering feedback & reviews', difficulty: 1 },
      { name: 'Creating client follow-up summaries', difficulty: 2 },
      { name: 'Sending post-service updates', difficulty: 1 },
      { name: 'Appointment confirmations or reminders', difficulty: 1 },
      { name: 'Internal handoffs or escalation tracking', difficulty: 2 },
      'Custom...'
    ]
  },
  salesMarketing: {
    id: 'salesMarketing',
    label: 'Sales, Marketing & Growth',
    subtitle: 'Revenue & Content Generation',
    color: 'primary',
    hasSubCategories: true,
    subCategories: {
      sales: {
        id: 'sales',
        label: 'Sales Process & Follow-Up',
        processes: [
          { name: 'Logging call or meeting notes', difficulty: 1 },
          { name: 'Lead research or data cleanup', difficulty: 2 },
          { name: 'Email or outreach sequencing', difficulty: 2 },
          { name: 'Pipeline forecasting updates', difficulty: 3 },
          { name: 'Opportunity follow-up reminders', difficulty: 1 },
          { name: 'CRM tagging or task creation', difficulty: 2 },
          'Custom...'
        ]
      },
      content: {
        id: 'content',
        label: 'Content Creation & Promotion',
        processes: [
          { name: 'Blog or article writing (drafting, editing, posting)', difficulty: 2 },
          { name: 'Website or app content updates (text or images)', difficulty: 2 },
          { name: 'Social media post creation (graphics, captions)', difficulty: 2 },
          { name: 'Video production (strategy, scripting, editing, posting)', difficulty: 4 },
          { name: 'Ad or campaign creative (copy, headlines, visuals)', difficulty: 3 },
          { name: 'Marketing asset management (tracking, file updates)', difficulty: 2 },
          'Custom...'
        ]
      },
      analytics: {
        id: 'analytics',
        label: 'Analytics & Optimization',
        processes: [
          { name: 'Campaign or ad performance reports', difficulty: 2 },
          { name: 'SEO keyword research & updates', difficulty: 2 },
          { name: 'Content repurposing (post → email/blog)', difficulty: 2 },
          { name: 'Channel performance summaries', difficulty: 2 },
          'Custom...'
        ]
      }
    }
  },
  team: {
    id: 'team',
    label: 'Team & Decision-Making',
    subtitle: 'Collaboration & Reporting',
    color: 'primary',
    processes: [
      { name: 'KPI or performance summary prep', difficulty: 2 },
      { name: 'Meeting note → task follow-up', difficulty: 1 },
      { name: 'Budget or expense reporting', difficulty: 2 },
      { name: 'Cross-tool task syncing', difficulty: 3 },
      { name: 'Hiring candidate summaries', difficulty: 2 },
      { name: 'Data cleanup or quality checks', difficulty: 2 },
      { name: 'Weekly dashboard updates', difficulty: 2 },
      'Custom...'
    ]
  }
};

// Helper function to get all processes for a category
export const getProcessesForCategory = (categoryId, subCategoryId = null) => {
  const category = ROI_CATEGORIES[categoryId];
  if (!category) return [];
  
  if (category.hasSubCategories && subCategoryId) {
    return category.subCategories[subCategoryId]?.processes || [];
  }
  
  return category.processes || [];
};

// Helper function to get difficulty for a specific process
export const getProcessDifficulty = (categoryId, processName, subCategoryId = null) => {
  const processes = getProcessesForCategory(categoryId, subCategoryId);
  const processItem = processes.find(p => typeof p === 'object' && p.name === processName);
  return processItem?.difficulty || 3;
};

// Helper function to get category label
export const getCategoryLabel = (categoryId) => {
  return ROI_CATEGORIES[categoryId]?.label || '';
};

// Helper function to check if category has subcategories
export const hasSubCategories = (categoryId) => {
  return ROI_CATEGORIES[categoryId]?.hasSubCategories || false;
};

// Get all categories for dropdown
export const getCategoryOptions = () => {
  return Object.keys(ROI_CATEGORIES).map(key => ({
    value: key,
    label: ROI_CATEGORIES[key].label,
    subtitle: ROI_CATEGORIES[key].subtitle
  }));
};

// Get sub-category options for Sales/Marketing
export const getSubCategoryOptions = (categoryId) => {
  const category = ROI_CATEGORIES[categoryId];
  if (!category?.hasSubCategories) return [];
  
  return Object.keys(category.subCategories).map(key => ({
    value: key,
    label: category.subCategories[key].label
  }));
};
